import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Button } from '~/components/button';

export const Route = createFileRoute('/_authed/founder/checkin')({
  component: WeeklyCheckin,
});

interface Checkin {
  weekStart: string;
  energy: number;
  win: string;
  release: string;
  submittedAt?: string;
}

async function fetchJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const r = await fetch(path, {
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
    ...init,
  });
  if (!r.ok) throw new Error(`${path} -> ${r.status}`);
  return (await r.json()) as T;
}

function WeeklyCheckin() {
  const qc = useQueryClient();
  const latest = useQuery({
    queryKey: ['checkin-latest'],
    queryFn: () => fetchJson<{ checkin: Checkin | null }>('/api/founder/checkin/latest'),
  });
  const history = useQuery({
    queryKey: ['checkin-history'],
    queryFn: () => fetchJson<{ checkins: Checkin[] }>('/api/founder/checkin/history'),
  });

  const [energy, setEnergy] = useState(5);
  const [win, setWin] = useState('');
  const [release, setRelease] = useState('');

  useEffect(() => {
    if (latest.data?.checkin) {
      // Pre-fill if this week already has one submitted (idempotent overwrite).
      const c = latest.data.checkin;
      const thisWeek = currentWeekStart();
      if (c.weekStart === thisWeek) {
        setEnergy(c.energy);
        setWin(c.win);
        setRelease(c.release);
      }
    }
  }, [latest.data?.checkin]);

  const submit = useMutation({
    mutationFn: () =>
      fetchJson<{ ok: boolean }>('/api/founder/checkin', {
        method: 'POST',
        body: JSON.stringify({ energy, win: win.trim() || undefined, release: release.trim() || undefined }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['checkin-latest'] });
      qc.invalidateQueries({ queryKey: ['checkin-history'] });
    },
  });

  return (
    <section className="mx-auto max-w-2xl px-4 py-12">
      <h1 className="text-2xl font-semibold tracking-tight">weekly check-in</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        one minute. three questions. you can revisit it any time before sunday — submitting again overwrites this week.
      </p>

      <form
        className="mt-8 space-y-6"
        onSubmit={(e) => {
          e.preventDefault();
          submit.mutate();
        }}
      >
        <div>
          <label className="text-sm font-medium">energy this week — {energy}/10</label>
          <input
            type="range"
            min={1}
            max={10}
            value={energy}
            onChange={(e) => setEnergy(Number(e.target.value))}
            className="mt-2 w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>flat</span>
            <span>steady</span>
            <span>lit</span>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium">one win — small counts</label>
          <textarea
            value={win}
            onChange={(e) => setWin(e.target.value)}
            rows={2}
            placeholder="what landed? what worked? what did you do that you can name?"
            className="mt-2 w-full rounded-[var(--radius)] border border-input bg-background p-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        <div>
          <label className="text-sm font-medium">one release — what you'd like to set down</label>
          <textarea
            value={release}
            onChange={(e) => setRelease(e.target.value)}
            rows={2}
            placeholder="a worry, a grievance, a story that didn't help. write it down. that's enough."
            className="mt-2 w-full rounded-[var(--radius)] border border-input bg-background p-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        <Button type="submit" disabled={submit.isPending}>
          {submit.isPending ? 'saving…' : 'check in'}
        </Button>
      </form>

      <section className="mt-12">
        <h2 className="text-sm uppercase tracking-wide text-muted-foreground">recent weeks</h2>
        <ul className="mt-3 space-y-2 text-sm">
          {(history.data?.checkins ?? []).map((c) => (
            <li key={c.weekStart} className="rounded-[var(--radius)] border border-border p-3">
              <div className="flex items-baseline justify-between text-xs text-muted-foreground">
                <span>{new Date(c.weekStart).toLocaleDateString()}</span>
                <span>energy {c.energy}/10</span>
              </div>
              {c.win && <p className="mt-1"><span className="text-muted-foreground">win — </span>{c.win}</p>}
              {c.release && (
                <p className="mt-1">
                  <span className="text-muted-foreground">release — </span>
                  {c.release}
                </p>
              )}
            </li>
          ))}
          {(history.data?.checkins ?? []).length === 0 && (
            <li className="text-muted-foreground">no check-ins yet.</li>
          )}
        </ul>
      </section>
    </section>
  );
}

function currentWeekStart(): string {
  const d = new Date();
  const day = d.getUTCDay() || 7;
  d.setUTCHours(0, 0, 0, 0);
  d.setUTCDate(d.getUTCDate() - (day - 1));
  return d.toISOString();
}
